import { Component, signal, inject, computed, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { LoadingOverlayComponent } from 'src/app/presentation/shared/loading-overlay.component';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

import {
  DevolucionCumplimiento,
  DevolucionCumplimientoFiltro
} from 'src/app/domain/model/devolucion-cumplimiento/devolucion-cumplimiento';
import { DevolucionCumplimientoRepository } from 'src/app/domain/service/devolucion-cumplimiento/devolucion-cumplimiento-repository';
import { HttpDevolucionCumplimientoRepository } from 'src/app/infrastructure/adapters/devolucion-cumplimiento/http-devolucion-cumplimiento.repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import {
  BusquedaClienteDialogComponent,
  ClienteSeleccionado
} from 'src/app/presentation/common/busquedas/busqueda-cliente-dialog.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorSeleccionado
} from 'src/app/presentation/common/busquedas/busqueda-asesor-dialog.component';
import {
  DEFAULT_ROWS_PER_PAGE,
  MAX_PAGE_SIZE,
  PAGE_SIZE_OPTIONS, MAX_JUSTIFICACION_ANULACION
} from 'src/app/common/constants-ejb';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { devolucionCumplimientosPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/**
 * Componente: Devolución de Cumplimientos
 *
 * Legacy: DevolucionCumplimientosViewImpl.java (~870 líneas)
 * Legacy Activity: DevolucionCumplimientosActivity.java
 *
 * Filtros: ID Cumplimiento, Orientación Servicios, ID Programación, Oficina, Cliente, Consultor
 * Grid: p-table con multi-select (checkbox)
 * Acciones: Buscar, Limpiar, Anular (abre modal con justificación)
 */
@Component({
  selector: 'app-devolucion-cumplimientos',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    CommonModule,
    FormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    DialogModule,
    TooltipModule,
    CheckboxModule,
    LoadingOverlayComponent,
    BusquedaPaginadorComponent,
    BusquedaClienteDialogComponent,
    BusquedaAsesorDialogComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [
    {
      provide: DevolucionCumplimientoRepository,
      useClass: HttpDevolucionCumplimientoRepository
    }
  ],
  template: `
    <!-- Spinner global mientras se busca o se anulan cumplimientos -->
    <app-loading-overlay [blocked]="loading() || anulando()" />

    <div class="p-3 sm:p-6 w-full">
      <!-- Header -->
      <div class="mb-6">
        <h1
          class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2"
        >
          📋 Devolución de Cumplimientos
        </h1>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Consulta y anulación de cumplimientos pendientes de devolución
        </p>
      </div>

      <!-- ===== FILTROS ===== -->
      <div
        class="border border-gray-200 dark:border-gray-700 rounded p-3 sm:p-4 mb-4 bg-gray-50 dark:bg-gray-800 transition-colors duration-300"
      >
        <div class="font-medium text-gray-700 dark:text-gray-200 mb-3 text-sm">
          Filtros de Búsqueda
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          <!-- ID Cumplimiento -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >ID Cumplimiento</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.ivacumplimientos"
              class="w-full"
              maxlength="50"
              (keyup.enter)="buscar()"
            />
          </div>
          <!-- Orientación de Servicios -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Orientación de Servicios</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.ivaorden"
              class="w-full"
              maxlength="50"
              (keyup.enter)="buscar()"
            />
          </div>
          <!-- ID Programación -->
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >ID Programación</label
            >
            <input
              pInputText
              [(ngModel)]="filtro.ivaprog"
              class="w-full"
              maxlength="50"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          <!-- Oficina -->
          <div class="min-w-0">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Oficina</label
            >
            <p-dropdown
              [(ngModel)]="filtro.inuoficina"
              [options]="oficinas()"
              optionLabel="dsoficina"
              optionValue="nmconsecutivo"
              placeholder="Seleccione..."
              [showClear]="true"
              [filter]="true"
              filterBy="dsoficina"
              filterPlaceholder="Buscar oficina..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              [style]="{ width: '100%' }"
              appendTo="body"
            >
            </p-dropdown>
          </div>
          <!-- Cliente (beneficiario) -->
          <div class="min-w-0">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Cliente</label
            >
            <div class="flex gap-1 min-w-0">
              <input
                pInputText
                [value]="clienteDisplay()"
                class="flex-1 min-w-0"
                readonly
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar cliente"
                (click)="mostrarBuscadorCliente = true"
              ></button>
              @if (clienteSeleccionado()) {
                <button
                  pButton
                  icon="pi pi-times"
                  class="p-button-sm p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                  pTooltip="Quitar cliente"
                  (click)="limpiarCliente()"
                ></button>
              }
            </div>
          </div>
          <!-- Consultor (asesor) -->
          <div class="min-w-0">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Consultor</label
            >
            <div class="flex gap-1 min-w-0">
              <input
                pInputText
                [value]="consultorDisplay()"
                class="flex-1 min-w-0"
                readonly
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar consultor"
                (click)="mostrarBuscadorConsultor = true"
              ></button>
              @if (consultorSeleccionado()) {
                <button
                  pButton
                  icon="pi pi-times"
                  class="p-button-sm p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                  pTooltip="Quitar consultor"
                  (click)="limpiarConsultor()"
                ></button>
              }
            </div>
          </div>
        </div>

        <!-- Botones -->
        <div class="flex flex-wrap gap-2 mt-4">
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-sm"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            icon="pi pi-eraser"
            label="Limpiar"
            class="p-button-sm p-button-secondary p-button-outlined"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </div>

      <!-- ===== MENSAJE ===== -->
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300':
              mensajeTipo() === 'info',
            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300':
              mensajeTipo() === 'warning',
            'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300':
              mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-check-circle': mensajeTipo() === 'info',
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- ===== TABLA DE RESULTADOS ===== -->
      @if (!loading() && items().length > 0) {
        <div class="mb-3">
          <button
            pButton
            icon="pi pi-times-circle"
            label="Anular"
            class="p-button-sm p-button-danger"
            pTooltip="Anular cumplimientos seleccionados"
            [disabled]="seleccionados().length === 0"
            (click)="abrirModalAnular()"
          ></button>
          <span class="ml-3 text-sm text-gray-600 dark:text-gray-300">
            {{ seleccionados().length }} seleccionado(s)
          </span>
        </div>

        @if (totalRegistros() > 0) {
          <div class="mb-3 text-sm text-gray-600 dark:text-gray-300">
            Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
            {{ totalRegistros() }}
          </div>
        }

        <div class="md:hidden space-y-3 mb-3">
          @for (item of items(); track item.rowKey ?? $index) {
            <article
              class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <h3
                  class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                >
                  Cumplimiento {{ textoTabla(item.cumplimiento) }}
                </h3>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{
                  textoTabla(formatearFechaProgramacion(item.fechaprogramacion))
                }}</span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Orientación:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.orden)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Programación:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.prog)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Consultor:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.asesor)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Cliente:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.beneficiario)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Tarea:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.tarea)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Tipo:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.tipo)
                  }}</span>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <p-button
                  [label]="
                    isItemSelected(item) ? 'Quitar selección' : 'Seleccionar'
                  "
                  [icon]="
                    isItemSelected(item) ? 'pi pi-check-circle' : 'pi pi-circle'
                  "
                  [outlined]="true"
                  severity="secondary"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="toggleSeleccionItem(item)"
                />

                @if (mostrarAccionProgramada(item)) {
                  <p-button
                    label="Cambiar a programada"
                    icon="pi pi-plus-circle"
                    severity="info"
                    [outlined]="true"
                    size="small"
                    styleClass="w-full min-h-[44px]"
                    (onClick)="devolverProgramacion(item)"
                  />
                }

                @if (mostrarAccionDevP8(item)) {
                  <div
                    class="sm:col-span-2 text-xs text-red-600 dark:text-red-400"
                  >
                    Dev P8 no disponible en esta migración.
                  </div>
                }
              </div>
            </article>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="cumplimientosTableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [value]="items()"
            [(selection)]="seleccion"
            [selectionMode]="cumplimientosTableSelectionMode()"
            (selectionChange)="onSelectionChange($event)"
            [dataKey]="cumplimientosTableDataKey()"
            [resizableColumns]="
              cumplimientosTableConfig().style.resizableColumns !== false
            "
            columnResizeMode="expand"
            [scrollable]="true"
            [scrollHeight]="
              cumplimientosTableConfig().responsive.scrollHeight || '400px'
            "
            [styleClass]="cumplimientosTableStyleClass()"
            [tableStyle]="{
              'min-width':
                cumplimientosTableConfig().responsive.minWidth || '119rem'
            }"
            [responsiveLayout]="'scroll'"
          >
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 3rem">
                  <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Id. Cumplimiento"
                    >Id. Cumplimiento</span
                  >
                </th>
                <th pResizableColumn>
                  <span
                    class="grid-header-text"
                    title="Orientación de Servicios"
                    >Orientación de Servicios</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Id. Programación"
                    >Id. Programación</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Consultor"
                    >Consultor</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Cliente">Cliente</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tarea">Tarea</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tipo">Tipo</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Fecha Programación"
                    >Fecha Programación</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Programada"
                    >Programada</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Dev P8">Dev P8</span>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>
                  <p-tableCheckbox [value]="item"></p-tableCheckbox>
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.cumplimiento)"
                    >{{ textoTabla(item.cumplimiento) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.orden)"
                    >{{ textoTabla(item.orden) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.prog)"
                    >{{ textoTabla(item.prog) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.asesor)"
                    >{{ textoTabla(item.asesor) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.beneficiario)"
                    >{{ textoTabla(item.beneficiario) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.tarea)"
                    >{{ textoTabla(item.tarea) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.tipo)"
                    >{{ textoTabla(item.tipo) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="
                      tituloTabla(
                        formatearFechaProgramacion(item.fechaprogramacion)
                      )
                    "
                    >{{
                      textoTabla(
                        formatearFechaProgramacion(item.fechaprogramacion)
                      )
                    }}</span
                  >
                </td>
                <td class="text-center">
                  @if (mostrarAccionProgramada(item)) {
                    <button
                      pButton
                      type="button"
                      icon="pi pi-plus-circle"
                      class="p-button-text p-button-sm p-0 text-blue-600 min-w-[44px] min-h-[44px]"
                      pTooltip="Cambiar a estado programada"
                      (click)="devolverProgramacion(item)"
                    ></button>
                  }
                </td>
                <td class="text-center">
                  @if (mostrarAccionDevP8(item)) {
                    <i
                      class="pi pi-times-circle text-red-500"
                      pTooltip="Dev P8 no disponible en esta migración"
                    ></i>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <div
          class="flex flex-wrap items-center justify-end gap-2 mt-3 px-1"
        >
          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPaginas()"
            [hasPrevious]="paginaActual() > 1"
            [hasNext]="paginaActual() < totalPaginas()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="pageSize()"
            (pageSizeChange)="onPageSizeChange($event)"
            (first)="irPrimeraPagina()"
            (previous)="paginaAnterior()"
            (next)="paginaSiguiente()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        </div>

        <p-dialog
          header="Anular Cumplimientos"
          [(visible)]="mostrarModalAnular"
          [modal]="true"
          [style]="{ width: '95vw', maxWidth: '500px' }"
          [closable]="true"
        >
          <div class="mb-3">
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1">
              Justificación <span class="text-red-500">*</span>
            </label>
            <textarea
              pInputTextarea
              [(ngModel)]="justificacion"
              (input)="justificacion = sanitizeTextarea(justificacion)"
              rows="4"
              class="w-full"
              [appContadorCaracteres]="maxJustificacionAnulacion"
              placeholder="Ingrese la justificación de la anulación..."
            ></textarea>
          </div>

          <ng-template pTemplate="footer">
            <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
              <button
                pButton
                label="Cancelar"
                icon="pi pi-times"
                class="p-button-outlined min-h-[44px]"
                (click)="cerrarModalAnular()"
              ></button>
              <button
                pButton
                label="Aceptar"
                icon="pi pi-check"
                class="min-h-[44px]"
                [disabled]="!justificacion.trim() || anulando()"
                (click)="confirmarAnulacion()"
              ></button>
            </div>
          </ng-template>
        </p-dialog>
      }

      <!-- ===== BUSCADOR CLIENTE ===== -->
      @if (mostrarBuscadorCliente) {
        <app-busqueda-cliente-dialog
          (onSelect)="onClienteSeleccionado($event)"
          (onClose)="mostrarBuscadorCliente = false"
        >
        </app-busqueda-cliente-dialog>
      }
      <!-- ===== BUSCADOR CONSULTOR / ASESOR ===== -->
      @if (mostrarBuscadorConsultor) {
        <app-asesor-search
          (onSelect)="onConsultorSeleccionado($event)"
          (onClose)="mostrarBuscadorConsultor = false"
        >
        </app-asesor-search>
      }
    </div>
  `
})
export class DevolucionCumplimientosComponent implements OnInit {
  readonly maxJustificacionAnulacion = MAX_JUSTIFICACION_ANULACION;
  private readonly devolucionRepo = inject(DevolucionCumplimientoRepository);
  private readonly oficinaRepo = inject(OficinaRepository);
  private readonly usuarioState = inject(UsuarioStateService);

  // XSS Sanitization - exponer función al template
  sanitizeTextarea = sanitizeTextarea;

  // === Filtros ===
  filtro: DevolucionCumplimientoFiltro = {};
  clienteSeleccionado = signal<ClienteSeleccionado | null>(null);
  consultorSeleccionado = signal<AsesorSeleccionado | null>(null);

  // === Estado ===
  items = signal<DevolucionCumplimiento[]>([]);
  loading = signal(false);
  anulando = signal(false);
  mensaje = signal('');
  mensajeTipo = signal<'info' | 'warning' | 'error'>('info');
  oficinas = signal<Oficina[]>([]);
  busquedaRealizada = signal(false);

  // === Selección ===
  seleccion: DevolucionCumplimiento[] = [];
  seleccionados = signal<DevolucionCumplimiento[]>([]);

  // === Modal Anular ===
  mostrarModalAnular = false;
  justificacion = '';

  // === Buscadores ===
  mostrarBuscadorCliente = false;
  mostrarBuscadorConsultor = false;

  // === Paginación ===
  totalRegistros = signal(0);
  paginaActual = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly cumplimientosTableConfig = computed(() =>
    devolucionCumplimientosPreset.createConfig({
      data: this.items() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-devolucion-cumplimientos',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      selection: {
        mode: 'multiple',
        selected: this.seleccion as unknown as Record<string, unknown>[],
        dataKey: 'rowKey'
      },
      pagination: {
        enabled: false,
        mode: 'server',
        pageSize: this.pageSize(),
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        currentPageReportTemplate:
          'Registros del {first} hasta el {last} de {totalRecords}'
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron cumplimientos.'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '119rem',
        scrollHeight: '400px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de devolucion de cumplimientos',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly cumplimientosTableStyleClass = computed(() => {
    const config = this.cumplimientosTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly cumplimientosTableSelectionMode = computed<
    'single' | 'multiple' | null
  >(() => {
    const mode = this.cumplimientosTableConfig().selection.mode;
    return mode === 'single' || mode === 'multiple' ? mode : null;
  });

  readonly cumplimientosTableDataKey = computed(() =>
    String(this.cumplimientosTableConfig().selection.dataKey || 'rowKey')
  );

  totalPaginas = computed(() => {
    const total = this.totalRegistros();
    return total > 0 ? Math.ceil(total / this.pageSize()) : 0;
  });

  registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  });

  registroFin = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return Math.min(
      this.registroInicio() + this.items().length - 1,
      this.totalRegistros()
    );
  });

  // === Display ===
  clienteDisplay = computed(() => {
    const c = this.clienteSeleccionado();
    return c ? `${c.numeroIdentificacion} - ${c.nombreComercial}` : '';
  });

  consultorDisplay = computed(() => {
    const c = this.consultorSeleccionado();
    if (!c) {
      return '';
    }

    const nombre =
      c.dsnombreComercial?.trim() ||
      [c.dsnombre1, c.dsnombre2, c.dsapellido1, c.dsapellido2]
        .filter((parte) => parte && parte.trim())
        .map((parte) => parte.trim())
        .join(' ') ||
      c.nmidentificacion;

    return `${c.nmidentificacion} - ${nombre}`;
  });

  ngOnInit(): void {
    this.cargarOficinas();
    this.cargarDatosUsuario();
  }

  private async cargarDatosUsuario(): Promise<void> {
    try {
      const usuario = await this.usuarioState.cargar();
      if (usuario?.nmconsecOficina) {
        this.filtro.inuoficina = usuario.nmconsecOficina;
      }
    } catch (e) {
      console.error('Error cargando datos del usuario', e);
    }
  }

  private async cargarOficinas(): Promise<void> {
    try {
      const data = await this.oficinaRepo.obtenerOficinas();
      this.oficinas.set(data);
    } catch (e) {
      console.error('Error cargando oficinas', e);
    }
  }

  async buscar(): Promise<void> {
    // Validación legacy: al menos uno de los filtros principales debe estar diligenciado
    const tieneIdCumplimiento = !!this.filtro.ivacumplimientos?.trim();
    const tieneOrientacion = !!this.filtro.ivaorden?.trim();
    const tieneIdProgramacion = !!this.filtro.ivaprog?.trim();
    const tieneCliente = !!this.clienteSeleccionado();
    const tieneConsultor = !!this.consultorSeleccionado();

    if (
      !tieneIdCumplimiento &&
      !tieneOrientacion &&
      !tieneIdProgramacion &&
      !tieneCliente &&
      !tieneConsultor
    ) {
      this.mensaje.set(
        'Por favor ingrese uno de los siguientes filtros: Id cumplimiento, orientación de servicio, Id programación, Cliente, Consultor'
      );
      this.mensajeTipo.set('warning');
      return;
    }

    this.loading.set(true);
    this.mensaje.set('');
    this.seleccion = [];
    this.seleccionados.set([]);
    this.paginaActual.set(1);
    this.busquedaRealizada.set(true);

    try {
      await this.cargarPagina();
    } catch (e: any) {
      this.mensaje.set(e?.message || 'Error al consultar');
      this.mensajeTipo.set('error');
      this.items.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  private async cargarPagina(): Promise<void> {
    const filtroConBuscadores: DevolucionCumplimientoFiltro = {
      ...this.filtro,
      inubeneficiario: this.clienteSeleccionado()?.nmconsecutivo,
      inuasesor: this.consultorSeleccionado()?.nmconsecutivo
    };

    let data = await this.devolucionRepo.listarCumplimientos(
      filtroConBuscadores,
      this.paginaActual() - 1,
      this.pageSize()
    );

    // Página vacía más allá de la primera (p. ej. se anularon registros entre
    // consultas): se vuelve a la primera para no ocultar toda la tabla.
    if (data.length === 0 && this.paginaActual() > 1) {
      this.paginaActual.set(1);
      data = await this.devolucionRepo.listarCumplimientos(
        filtroConBuscadores,
        0,
        this.pageSize()
      );
    }

    // Genera una clave única por fila para que el selection model de PrimeNG
    // no colapse filas con cumplimiento=null (solo programaciones) entre sí.
    const conRowKey = data.map((d, idx) => ({
      ...d,
      rowKey: `c${d.cumplimiento ?? 'x'}-p${d.prog ?? 'x'}-${idx}`
    }));
    this.items.set(conRowKey);

    const totalSp = conRowKey[0]?.totalregistros;
    if (totalSp == null && conRowKey.length > 0) {
      // Fila sin totalregistros: se estima con lo ya visto para no perder el paginador.
      this.totalRegistros.set(
        (this.paginaActual() - 1) * this.pageSize() + conRowKey.length
      );
    } else {
      this.totalRegistros.set(Number(totalSp) > 0 ? Number(totalSp) : 0);
    }
  }

  limpiarFiltros(): void {
    this.filtro = {};
    this.clienteSeleccionado.set(null);
    this.consultorSeleccionado.set(null);
    this.items.set([]);
    this.seleccion = [];
    this.seleccionados.set([]);
    this.mensaje.set('');
    this.busquedaRealizada.set(false);
    this.totalRegistros.set(0);
    this.paginaActual.set(1);
  }

  onSelectionChange(selection: DevolucionCumplimiento[]): void {
    this.seleccionados.set(selection);
  }

  isItemSelected(item: DevolucionCumplimiento): boolean {
    return this.seleccion.some((seleccionado) =>
      this.esMismoItem(seleccionado, item)
    );
  }

  toggleSeleccionItem(item: DevolucionCumplimiento): void {
    const yaSeleccionado = this.isItemSelected(item);

    if (yaSeleccionado) {
      this.seleccion = this.seleccion.filter(
        (seleccionado) => !this.esMismoItem(seleccionado, item)
      );
    } else {
      this.seleccion = [...this.seleccion, item];
    }

    this.onSelectionChange([...this.seleccion]);
  }

  private esMismoItem(
    a: DevolucionCumplimiento,
    b: DevolucionCumplimiento
  ): boolean {
    if (a.rowKey && b.rowKey) {
      return a.rowKey === b.rowKey;
    }

    return a.cumplimiento === b.cumplimiento && a.prog === b.prog;
  }

  // === Buscadores ===
  onClienteSeleccionado(cliente: ClienteSeleccionado): void {
    this.clienteSeleccionado.set(cliente);
    this.mostrarBuscadorCliente = false;
  }

  limpiarCliente(): void {
    this.clienteSeleccionado.set(null);
  }

  onConsultorSeleccionado(consultor: AsesorSeleccionado): void {
    this.consultorSeleccionado.set(consultor);
    this.mostrarBuscadorConsultor = false;
  }

  limpiarConsultor(): void {
    this.consultorSeleccionado.set(null);
  }

  // === Modal Anular ===
  abrirModalAnular(): void {
    this.justificacion = '';
    this.mostrarModalAnular = true;
  }

  cerrarModalAnular(): void {
    this.mostrarModalAnular = false;
    this.justificacion = '';
  }

  async confirmarAnulacion(): Promise<void> {
    if (!this.justificacion.trim()) return;

    this.anulando.set(true);
    try {
      // Replica de DevolucionCumplimientosViewImpl.getListaCumplimientos()
      // y getListaProgramaciones() del legacy:
      // - cumplimientos: filas seleccionadas con cumplimiento != null
      // - programaciones: filas seleccionadas SIN cumplimiento, con prog != null
      const seleccion = this.seleccionados();
      // Legacy: DevolucionCumplimientosViewImpl.getListaCumplimientos()
      // Filas con cumplimiento != null \u2192 van al SP_PSC_DEV_CUMPLIMIENTOS
      const cumplimientos = seleccion
        .filter((s) => s.cumplimiento != null && s.cumplimiento > 0)
        .map((s) => s.cumplimiento as number)
        .join(',');
      // Legacy: DevolucionCumplimientosViewImpl.getListaProgramaciones()
      // Filas sin cumplimiento pero con prog != null \u2192 UPDATE CDESTADO='0015'
      const programaciones = seleccion
        .filter(
          (s) =>
            (s.cumplimiento == null || s.cumplimiento === 0) &&
            s.prog != null &&
            s.prog > 0
        )
        .map((s) => s.prog as number);

      // Legacy: DevolucionCumplimientosViewImpl.getListaCumplimientosCorreo()
      // Filas con cumplimiento != null y tipo === 'CAMPO' \u2192 notificaci\u00f3n por correo
      const cumplimientosCampo = seleccion.filter(
        (s) =>
          s.cumplimiento != null &&
          s.cumplimiento > 0 &&
          s.tipo === 'CAMPO'
      );

      const dniUsuario = (await this.usuarioState.cargar())?.dni || '';

      const resultado = await this.devolucionRepo.anularCumplimientos({
        cumplimientos,
        dniUsuario,
        observacion: this.justificacion,
        programaciones,
        cumplimientosCampo
      });

      this.cerrarModalAnular();

      if (resultado.exito) {
        // Componer mensaje seg\u00fan lo que se haya anulado (cumplimientos y/o programaciones)
        const partes: string[] = [];
        if (cumplimientos && resultado.cumplDevueltos > 0) {
          partes.push(
            `Se anularon ${resultado.cumplDevueltos} cumplimiento(s)`
          );
        }
        if (resultado.msgAdicional?.trim()) {
          partes.push(resultado.msgAdicional.trim());
        }
        const mensajeExito =
          partes.length > 0
            ? partes.join('. ')
            : 'Devoluci\u00f3n realizada con \u00e9xito.';

        // Refresca la tabla primero (buscar() limpia mensaje), luego setea el mensaje de \u00e9xito
        await this.buscar();
        this.mensaje.set(mensajeExito);
        this.mensajeTipo.set('info');
      } else {
        this.mensaje.set(
          resultado.mensajeError || 'Error al anular cumplimientos'
        );
        this.mensajeTipo.set('error');
      }
    } catch (e: any) {
      this.mensaje.set(e?.message || 'Error al anular cumplimientos');
      this.mensajeTipo.set('error');
    } finally {
      this.anulando.set(false);
    }
  }

  formatearFechaProgramacion(fecha: string | null | undefined): string {
    if (!fecha) {
      return '-';
    }

    const soloFecha = fecha.split('T')[0]?.trim();
    return soloFecha ? soloFecha.replace(/-/g, '/') : '-';
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  mostrarAccionProgramada(item: DevolucionCumplimiento): boolean {
    const cantidad = Number(item.cantidad ?? 0);
    return (
      item.prog != null && ((item.cumplimiento ?? 0) === 0 || cantidad === 0)
    );
  }

  mostrarAccionDevP8(item: DevolucionCumplimiento): boolean {
    return item.idEstado !== 'RE' && item.idp8 != null;
  }

  async devolverProgramacion(item: DevolucionCumplimiento): Promise<void> {
    if (item.prog == null) {
      return;
    }

    this.anulando.set(true);
    try {
      const dniUsuario = (await this.usuarioState.cargar())?.dni || '';
      const resultado = await this.devolucionRepo.anularCumplimientos({
        cumplimientos: '',
        dniUsuario,
        observacion: '',
        programaciones: [item.prog],
        cumplimientosCampo: []
      });

      if (!resultado.exito) {
        this.mensaje.set(
          resultado.mensajeError ||
            'Error al cambiar la programación a estado programada'
        );
        this.mensajeTipo.set('error');
        return;
      }

      await this.cargarPagina();
      this.seleccion = [];
      this.seleccionados.set([]);
      this.mensaje.set(
        `La programación ${item.prog} se cambió a estado programada.`
      );
      this.mensajeTipo.set('info');
    } catch (e: any) {
      this.mensaje.set(
        e?.message || 'Error al cambiar la programación a estado programada'
      );
      this.mensajeTipo.set('error');
    } finally {
      this.anulando.set(false);
    }
  }

  // === Paginación ===
  /**
   * Navega a una página (1-based) acotada a 1..totalPaginas. Si la consulta falla
   * restaura la página anterior para que el indicador coincida con los datos.
   */
  private async irAPagina(pagina: number): Promise<void> {
    const anterior = this.paginaActual();
    this.paginaActual.set(
      Math.min(Math.max(1, pagina), Math.max(1, this.totalPaginas()))
    );
    this.loading.set(true);
    try {
      await this.cargarPagina();
    } catch (e: any) {
      this.paginaActual.set(anterior);
      this.mensaje.set(e?.message || 'Error al consultar');
      this.mensajeTipo.set('error');
    } finally {
      this.loading.set(false);
    }
  }

  irPrimeraPagina(): Promise<void> {
    return this.irAPagina(1);
  }

  paginaAnterior(): Promise<void> {
    return this.irAPagina(this.paginaActual() - 1);
  }

  paginaSiguiente(): Promise<void> {
    return this.irAPagina(this.paginaActual() + 1);
  }

  irUltimaPagina(): Promise<void> {
    return this.irAPagina(this.totalPaginas());
  }

  async onPageSizeChange(newPageSize: number): Promise<void> {
    const size = Number(newPageSize);
    if (
      !Number.isFinite(size) ||
      size <= 0 ||
      size > MAX_PAGE_SIZE ||
      size === this.pageSize()
    ) {
      return;
    }

    this.pageSize.set(size);
    if (this.busquedaRealizada()) {
      await this.irAPagina(1);
    } else {
      this.paginaActual.set(1);
    }
  }
}
